/** Thrown with a user-facing message when an image cannot be prepared for upload. */
export class ImageConversionError extends Error {}

export const imageConversionFailedMessage =
  "We couldn't convert this image. Try a JPEG or PNG.";

type ConvertibleKind = "heic" | "tiff";

const convertibleByExtension: Record<string, ConvertibleKind> = {
  heic: "heic",
  heif: "heic",
  tif: "tiff",
  tiff: "tiff",
};

const convertibleByType: Record<string, ConvertibleKind> = {
  "image/heic": "heic",
  "image/heif": "heic",
  "image/tiff": "tiff",
};

function convertibleKind(file: File): ConvertibleKind | null {
  const byType = convertibleByType[file.type.toLowerCase()];
  if (byType) return byType;
  // Windows browsers often report an empty type for HEIC, so fall back to the
  // extension.
  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
  return convertibleByExtension[extension] ?? null;
}

/** True for HEIC/HEIF and TIFF files, which browsers cannot display. */
export function isConvertibleImage(file: File) {
  return convertibleKind(file) !== null;
}

// Keep decoded RGBA data near 48 MB before canvas and decoder buffers.
const maxTiffPixels = 12_000_000;

function renamedWithExtension(fileName: string, extension: string) {
  const dot = fileName.lastIndexOf(".");
  return `${dot > 0 ? fileName.slice(0, dot) : fileName}.${extension}`;
}

async function heicToJpeg(file: File) {
  const { default: heic2any } = await import("heic2any");
  const converted = await heic2any({
    blob: file,
    toType: "image/jpeg",
    quality: 0.9,
  });
  // A multi-image HEIC returns an array; the first image is used.
  const blob = Array.isArray(converted) ? converted[0] : converted;
  if (!blob) throw new Error("HEIC conversion returned no image");
  return new File([blob], renamedWithExtension(file.name, "jpg"), {
    type: "image/jpeg",
    lastModified: file.lastModified,
  });
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality?: number,
) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob ? resolve(blob) : reject(new Error("Canvas export failed")),
      type,
      quality,
    );
  });
}

/**
 * Decodes the first page of a TIFF to PNG. Falls back to JPEG when the PNG is
 * larger than `maxBytes`, since photographic scans compress far better as JPEG.
 */
async function tiffToImage(file: File, maxBytes: number) {
  const utifModule = await import("utif2");
  const UTIF = utifModule.default ?? utifModule;
  const buffer = await file.arrayBuffer();
  const [firstPage] = UTIF.decode(buffer);
  if (!firstPage) throw new Error("TIFF has no image");
  // Dimensions are readable before decompression; a small compressed file can
  // still expand to more pixels than a browser tab can hold.
  const [declaredWidth = 0] = (firstPage.t256 as number[] | undefined) ?? [];
  const [declaredHeight = 0] = (firstPage.t257 as number[] | undefined) ?? [];
  if (declaredWidth * declaredHeight > maxTiffPixels)
    throw new Error("TIFF is too large to decode");
  UTIF.decodeImage(buffer, firstPage);
  const { width, height } = firstPage;
  if (!width || !height) throw new Error("TIFF has no pixels");

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas is unavailable");
  const rgba = UTIF.toRGBA8(firstPage);
  if (!(rgba.buffer instanceof ArrayBuffer))
    throw new Error("TIFF pixels use an unsupported buffer");
  context.putImageData(
    new ImageData(
      new Uint8ClampedArray(rgba.buffer, rgba.byteOffset, rgba.byteLength),
      width,
      height,
    ),
    0,
    0,
  );

  const png = await canvasToBlob(canvas, "image/png");
  if (png.size <= maxBytes)
    return new File([png], renamedWithExtension(file.name, "png"), {
      type: "image/png",
      lastModified: file.lastModified,
    });
  const jpeg = await canvasToBlob(canvas, "image/jpeg", 0.9);
  return new File([jpeg], renamedWithExtension(file.name, "jpg"), {
    type: "image/jpeg",
    lastModified: file.lastModified,
  });
}

/**
 * Converts HEIC/HEIF to JPEG and TIFF to PNG (JPEG when the PNG is too large)
 * in the browser, so the server only receives formats it already accepts.
 * Other files are returned untouched. Decoders load on demand. Multi-page TIFFs
 * keep the first page only.
 *
 * @throws {ImageConversionError} when decoding fails or the result exceeds `maxBytes`.
 */
export async function convertImageForUpload(
  file: File,
  maxBytes: number,
  tooLargeMessage: string,
): Promise<File> {
  const kind = convertibleKind(file);
  if (!kind) return file;

  let converted: File;
  try {
    converted =
      kind === "heic"
        ? await heicToJpeg(file)
        : await tiffToImage(file, maxBytes);
  } catch {
    throw new ImageConversionError(imageConversionFailedMessage);
  }
  if (converted.size === 0)
    throw new ImageConversionError(imageConversionFailedMessage);
  if (converted.size > maxBytes)
    throw new ImageConversionError(tooLargeMessage);
  return converted;
}
